import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw, Loader2, Ban, ChevronLeft, CheckCircle2, Clock, Send, X, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECIPIENT_PAGE_SIZE = 50;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const STATUS_STYLE = {
  queued:     { label: 'Queued',    cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40',  Icon: Clock },
  sending:    { label: 'Sending',   cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40', Icon: Send },
  completed:  { label: 'Completed', cls: 'bg-green-500/15 text-green-300 border-green-500/40', Icon: CheckCircle2 },
  cancelled:  { label: 'Cancelled', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/40',  Icon: Ban },
  failed:     { label: 'Failed',    cls: 'bg-red-500/15 text-red-300 border-red-500/40',    Icon: AlertTriangle },
};

const RECIPIENT_STATUS_STYLE = {
  pending:                { label: 'Pending',      cls: 'text-blue-300' },
  in_flight:              { label: 'Sending…',     cls: 'text-amber-300' },
  sent:                   { label: 'Sent',         cls: 'text-green-300' },
  failed:                 { label: 'Failed',       cls: 'text-red-300' },
  cancelled:              { label: 'Cancelled',    cls: 'text-gray-400' },
  skipped_unsubscribed:   { label: 'Unsubscribed', cls: 'text-gray-400' },
};

function StatusPill({ status }) {
  const spec = STATUS_STYLE[status] || { label: status, cls: 'bg-gray-700 text-gray-300 border-gray-600', Icon: Mail };
  const Icon = spec.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${spec.cls}`} data-testid={`campaign-status-${status}`}>
      <Icon className="w-3 h-3" />
      {spec.label}
    </span>
  );
}

function ProgressBar({ sent, failed, total }) {
  const sentPct = total > 0 ? Math.round((sent / total) * 100) : 0;
  const failedPct = total > 0 ? Math.round((failed / total) * 100) : 0;
  return (
    <div className="w-full">
      <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden flex">
        <div className="h-full bg-green-500" style={{ width: `${sentPct}%` }} title={`${sent} sent`} />
        <div className="h-full bg-red-500" style={{ width: `${failedPct}%` }} title={`${failed} failed`} />
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5">
        {sent.toLocaleString()} sent · {failed.toLocaleString()} failed · {total.toLocaleString()} total
      </div>
    </div>
  );
}

export { StatusPill, ProgressBar, formatDateTime, authHeaders, RECIPIENT_STATUS_STYLE };

export default function CampaignDetailDrawer({ campaignId, onClose, onCancelled }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recipientPage, setRecipientPage] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: String(recipientPage * RECIPIENT_PAGE_SIZE),
        limit: String(RECIPIENT_PAGE_SIZE),
      });
      const r = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}?${params}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Failed to load campaign (${r.status})`);
      const data = await r.json();
      setDetail(data);
    } catch (e) {
      setError(e.message || 'Failed to load campaign detail');
      logger.error('[CampaignsHistory] detail error', e);
    } finally {
      setLoading(false);
    }
  }, [campaignId, recipientPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!detail) return;
    const status = detail.campaign?.status;
    if (status !== 'queued' && status !== 'sending') return;
    const interval = setInterval(() => load(), 8000);
    return () => clearInterval(interval);
  }, [detail, load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCancel = async () => {
    if (!detail?.campaign) return;
    if (!window.confirm(`Cancel campaign "${detail.campaign.subject}"? Pending recipients will be skipped.`)) return;
    setCancelling(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}/cancel`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`Cancel failed (${r.status})`);
      toast.success('Campaign cancelled');
      onCancelled?.();
      load();
    } catch (e) {
      toast.error(e.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const failedCount = detail?.recipients?.status_counts?.failed || 0;

  const handleRetryFailed = async () => {
    if (!detail?.campaign || failedCount === 0) return;
    if (!window.confirm(`Re-queue ${failedCount} failed recipient${failedCount === 1 ? '' : 's'} for "${detail.campaign.subject}"?`)) return;
    setRetrying(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}/retry-failed`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!r.ok) {
        const txt = await r.text();
        let msg = `Retry failed (${r.status})`;
        try { msg = JSON.parse(txt).detail || msg; } catch (e) { /* ignore */ }
        throw new Error(msg);
      }
      const data = await r.json();
      if (data.retried_count === 0) {
        toast.info(data.message || 'Nothing to retry');
      } else {
        toast.success(`Re-queued ${data.retried_count} recipient${data.retried_count === 1 ? '' : 's'}`);
        onCancelled?.();
      }
      load();
    } catch (e) {
      toast.error(e.message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const c = detail?.campaign;
  const recipients = detail?.recipients;
  const counts = recipients?.status_counts || {};
  const canCancel = c && (c.status === 'queued' || c.status === 'sending');
  const canRetry = c && (c.status === 'completed' || c.status === 'failed') && failedCount > 0;
  const recTotal = recipients?.total || 0;
  const pageCount = Math.max(1, Math.ceil(recTotal / RECIPIENT_PAGE_SIZE));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-3"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="campaign-detail-overlay"
    >
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="campaign-detail-modal">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="campaign-detail-back" aria-label="Back">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-base font-semibold text-white">Campaign detail</h3>
          </div>
          <div className="flex items-center gap-2">
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:bg-gray-700 text-white text-sm rounded-lg flex items-center gap-1.5"
                data-testid="campaign-detail-cancel"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                Cancel
              </button>
            )}
            {canRetry && (
              <button
                onClick={handleRetryFailed}
                disabled={retrying}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 text-white text-sm rounded-lg flex items-center gap-1.5"
                data-testid="campaign-detail-retry-failed"
                title={`Re-queue ${failedCount} failed recipient${failedCount === 1 ? '' : 's'}`}
              >
                {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Retry failed ({failedCount})
              </button>
            )}
            <button onClick={load} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg flex items-center gap-1.5" data-testid="campaign-detail-refresh">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="campaign-detail-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {loading && !detail && (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-green-400" /></div>
          )}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
          )}
          {c && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Subject</div>
                  <div className="text-sm text-white" data-testid="campaign-detail-subject">{c.subject}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Status</div>
                    <StatusPill status={c.status} />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Sent from</div>
                  <div className="text-sm text-gray-200">{c.sender_persona}@myinvestfolio.io</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Created by</div>
                  <div className="text-sm text-gray-200">{c.created_by_name || '—'} <span className="text-gray-500">({c.created_by_role})</span></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Created</div>
                  <div className="text-sm text-gray-200">{formatDateTime(c.created_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Completed</div>
                  <div className="text-sm text-gray-200">{formatDateTime(c.completed_at)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 rounded-lg bg-gray-900 border border-gray-700">
                  <div className="text-[10px] uppercase text-gray-400">Audience</div>
                  <div className="text-lg font-bold text-white">{(c.audience_count || 0).toLocaleString()}</div>
                </div>
                <div className="p-2 rounded-lg bg-gray-900 border border-gray-700">
                  <div className="text-[10px] uppercase text-gray-400">Sent</div>
                  <div className="text-lg font-bold text-green-400">{(c.sent_count || 0).toLocaleString()}</div>
                </div>
                <div className="p-2 rounded-lg bg-gray-900 border border-gray-700">
                  <div className="text-[10px] uppercase text-gray-400">Failed</div>
                  <div className="text-lg font-bold text-red-400">{(c.failed_count || 0).toLocaleString()}</div>
                </div>
                <div className="p-2 rounded-lg bg-gray-900 border border-gray-700">
                  <div className="text-[10px] uppercase text-gray-400">Suppressed</div>
                  <div className="text-lg font-bold text-gray-400">{(c.suppressed_count || 0).toLocaleString()}</div>
                </div>
              </div>

              <ProgressBar sent={c.sent_count || 0} failed={c.failed_count || 0} total={c.audience_count || 0} />

              {c.last_error && (
                <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
                  Latest error: {c.last_error}
                </div>
              )}

              <details className="rounded-lg border border-gray-700 bg-gray-900/30">
                <summary className="px-3 py-2 cursor-pointer text-xs text-gray-300 hover:bg-gray-700/30 select-none">
                  Message body (click to expand)
                </summary>
                <pre className="p-3 text-xs text-gray-300 whitespace-pre-wrap font-mono">{c.body_text || '(empty)'}</pre>
              </details>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wide text-gray-400">
                    Recipients ({recTotal.toLocaleString()})
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    {Object.entries(counts).map(([k, v]) => (
                      <span key={k} className={RECIPIENT_STATUS_STYLE[k]?.cls || 'text-gray-400'}>
                        {(RECIPIENT_STATUS_STYLE[k]?.label || k)}: {v}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-700 overflow-x-auto" data-testid="campaign-detail-recipients">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-900 text-gray-400">
                      <tr>
                        <th className="text-left py-2 px-3">Recipient</th>
                        <th className="text-left py-2 px-3">Country</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Sent at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(recipients?.items || []).map((r, i) => {
                        const sty = RECIPIENT_STATUS_STYLE[r.status] || { label: r.status, cls: 'text-gray-400' };
                        return (
                          <tr key={`${r.user_id}-${i}`} className="border-t border-gray-700/50">
                            <td className="py-2 px-3">
                              <div className="text-white break-all">{r.email || r.user_id}</div>
                              {r.name && <div className="text-gray-500 text-[10px]">{r.name}</div>}
                            </td>
                            <td className="py-2 px-3 text-gray-300">{r.country || '—'}</td>
                            <td className={`py-2 px-3 ${sty.cls}`}>{sty.label}</td>
                            <td className="py-2 px-3 text-gray-400">{formatDateTime(r.sent_at)}</td>
                          </tr>
                        );
                      })}
                      {recipients?.items?.length === 0 && (
                        <tr><td colSpan={4} className="py-6 text-center text-gray-500">No recipients to show.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {pageCount > 1 && (
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <button
                      onClick={() => setRecipientPage(Math.max(0, recipientPage - 1))}
                      disabled={recipientPage === 0}
                      className="px-2 py-1 bg-gray-700 disabled:opacity-40 text-white rounded"
                      data-testid="campaign-detail-prev"
                    >Prev</button>
                    <span className="text-gray-400">Page {recipientPage + 1} of {pageCount}</span>
                    <button
                      onClick={() => setRecipientPage(Math.min(pageCount - 1, recipientPage + 1))}
                      disabled={recipientPage >= pageCount - 1}
                      className="px-2 py-1 bg-gray-700 disabled:opacity-40 text-white rounded"
                      data-testid="campaign-detail-next"
                    >Next</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
